
import { motion } from 'framer-motion';
import { ArrowRight, Brain, Lightbulb, Users, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { PageLayout } from '@/components/layout/PageLayout';

const Index = () => {
  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 to-transparent opacity-70" />
        <div className="container mx-auto px-4 py-16 md:py-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-sm text-blue-600 mb-4">
                <span className="text-xs font-medium">Elevate your AI expertise</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight font-lexend text-balance">
                Your AI Workout Routine for C-Level Excellence
              </h1>
              <p className="text-xl text-muted-foreground max-w-lg">
                Enhance your AI capabilities with just 20 minutes a day. Stay ahead of the curve and lead with confidence in the AI-driven world.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button asChild size="lg" className="group">
                  <Link to="/workout">
                    Start Your AI Workout
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/learn">Explore Training Modules</Link>
                </Button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <div className="relative aspect-square w-full max-w-md mx-auto">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-100 to-indigo-100 animate-pulse-slow" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <Brain className="h-32 w-32 text-primary animate-float" />
                </div>
                
                {/* Floating elements */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="absolute top-12 left-12 glass-card px-4 py-3 rounded-xl"
                >
                  <p className="text-sm font-medium">Daily AI Insights</p>
                </motion.div>
                
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.6 }}
                  className="absolute bottom-16 right-12 glass-card px-4 py-3 rounded-xl"
                >
                  <p className="text-sm font-medium">Track Progress</p>
                </motion.div>
                
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                  className="absolute bottom-32 left-20 glass-card px-4 py-3 rounded-xl"
                >
                  <p className="text-sm font-medium">AI Challenges</p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold font-lexend mb-4">Why C-Level AI Training Matters</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Just 20 minutes a day can transform how you leverage AI in your leadership role.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard 
              icon={<Lightbulb className="h-6 w-6" />}
              title="Early Gains"
              description="Understand short and long-term AI opportunities before your competitors, giving you a strategic advantage."
            />
            <FeatureCard 
              icon={<Users className="h-6 w-6" />}
              title="Team Buy-In"
              description="Lead by example. When your team sees you embracing AI, they'll feel encouraged to explore and adopt it too."
            />
            <FeatureCard 
              icon={<Target className="h-6 w-6" />}
              title="Risk Management"
              description="Develop a nuanced understanding of AI risks and flaws through hands-on experience."
            />
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold font-lexend mb-6">Ready to Start Your AI Workout?</h2>
          <p className="text-muted-foreground max-w-lg mx-auto mb-8">
            Join other C-Level executives who have transformed their leadership with our AI training program.
          </p>
          <Button asChild size="lg">
            <Link to="/workout">Begin Your 20-Minute Daily Routine</Link>
          </Button>
        </div>
      </section>
    </PageLayout>
  );
};

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const FeatureCard = ({ icon, title, description }: FeatureCardProps) => {
  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="glass-card rounded-xl p-6"
    >
      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-semibold font-lexend mb-2">{title}</h3>
      <p className="text-muted-foreground">{description}</p>
    </motion.div>
  );
};

export default Index;
